import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, List, ListItem, Link as MuiLink } from '@mui/material'
import MDEditor from '@uiw/react-md-editor'
import { Link } from 'react-router-dom'

const DocsComponent = () => {
    const { filename } = useParams<{ filename?: string }>()
    const navigate = useNavigate()
    const markdownRef = useRef<HTMLDivElement>(null)
    const [markdown, setMarkdown] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [availableDocs, setAvailableDocs] = useState<string[]>([])

    // List of available markdown files (you can expand this or fetch dynamically)
    const docFiles = ['README.md', 'UI.md']

    useEffect(() => {
        const loadDoc = async () => {
            setLoading(true)
            setError(null)

            try {
                const docFile = filename || 'README.md'
                // Add cache-busting query parameter to ensure fresh content
                // In development, this ensures changes are picked up immediately
                const cacheBuster = `?t=${Date.now()}`
                const response = await fetch(`/docs/${docFile}${cacheBuster}`, {
                    cache: 'no-store', // Don't cache at all
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                })

                if (!response.ok) {
                    throw new Error(`Failed to load ${docFile}`)
                }

                let text = await response.text()
                
                // Process markdown to convert relative links to absolute /docs/ paths
                // Convert ./filename.md or filename.md to /docs/filename.md
                text = text.replace(
                    /\[([^\]]+)\]\((\.\/)?([^)]+\.md)\)/g,
                    (match, linkText, relativePrefix, filePath) => {
                        // Extract just the filename if there's a path
                        const fileName = filePath.split('/').pop() || filePath
                        return `[${linkText}](/docs/${fileName})`
                    }
                )
                
                setMarkdown(text)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load documentation')
                setMarkdown('')
            } finally {
                setLoading(false)
            }
        }

        loadDoc()
        setAvailableDocs(docFiles)
    }, [filename])

    // Handle link clicks in markdown
    useEffect(() => {
        if (!markdownRef.current) return

        const handleLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            const link = target.closest('a')
            
            if (link && link.href) {
                try {
                    const url = new URL(link.href)
                    // Check if it's an internal docs link
                    if (url.pathname.startsWith('/docs/')) {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(url.pathname)
                    }
                } catch (err) {
                    // If URL parsing fails, check if it's a relative path
                    if (link.getAttribute('href')?.startsWith('/docs/')) {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(link.getAttribute('href') || '')
                    }
                }
            }
        }

        const markdownContainer = markdownRef.current
        markdownContainer.addEventListener('click', handleLinkClick)

        return () => {
            markdownContainer.removeEventListener('click', handleLinkClick)
        }
    }, [markdown, navigate])

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading documentation...</Typography>
            </Box>
        )
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color='error'>{error}</Typography>
                <Typography sx={{ mt: 2 }}>
                    Available documentation:
                </Typography>
                <List>
                    {availableDocs.map((doc) => (
                        <ListItem key={doc}>
                            <MuiLink component={Link} to={`/docs/${doc}`}>
                                {doc}
                            </MuiLink>
                        </ListItem>
                    ))}
                </List>
            </Box>
        )
    }

    return (
        <Box
            ref={markdownRef}
            sx={{
                p: 3,
                maxHeight: 'calc(100vh - 200px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                '& .wmde-markdown': {
                    backgroundColor: 'transparent',
                },
                '& .wmde-markdown a': {
                    cursor: 'pointer',
                },
            }}
        >
            <MDEditor.Markdown source={markdown} />
        </Box>
    )
}

export default DocsComponent

